import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { BillingPeriodSnapshot, PaymentStatus } from '../../types/tenancyTypes';

interface PaymentHistoryDropdownProps {
  billingHistory?: BillingPeriodSnapshot[];
}

const PaymentHistoryDropdown: React.FC<PaymentHistoryDropdownProps> = ({
  billingHistory = []
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!billingHistory || billingHistory.length === 0) {
    return null;
  }

  // Helper to show the latest activity in the collapsed header
  const latest = billingHistory[0]; // Assuming array is sorted DESC

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMonthName = (month: number) => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    return months[month - 1] || '';
  };

  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'partially-paid':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-700 text-sm">
            Billing History
          </span>

          {/* UX Enhancement: Summary Preview */}
          {!isOpen && latest && (
            <span className="text-xs text-gray-500 hidden sm:inline-block">
              Last: {getMonthName(latest.month)} {latest.year} —{' '}
              {formatCurrency(latest.amountPaid)}
              <span className="ml-1 opacity-75">({latest.paymentStatus})</span>
            </span>
          )}
        </div>

        {isOpen ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {billingHistory.map((period, index) => (
                <tr
                  key={`${period.year}-${period.month}-${index}`}
                  className="hover:bg-gray-50/50"
                >
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {getMonthName(period.month)} {period.year}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(period.rentAmount)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(period.amountPaid)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusColor(period.paymentStatus)}`}
                    >
                      {period.paymentStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryDropdown;
